import React from 'react';
import styled, { keyframes } from 'styled-components';

// Animation for modal appearance
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const TermsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 1rem;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
  overflow-y: auto;
`;

const TermsContent = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  color: #333;
  margin: auto;
  animation: ${slideUp} 0.4s ease-out;
  position: relative;

  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    max-height: 90vh;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    max-height: 95vh;
    border-radius: 8px;
  }
`;

const TermsTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #2c5aa0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.3;
  padding-right: 30px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const TermsList = styled.ol`
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 480px) {
    padding-left: 1.25rem;
  }
`;

const TermsListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  padding-left: 0.5rem;

  strong {
    color: #2c5aa0;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
    line-height: 1.5;
  }
`;

const TermsParagraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;

  @media (max-width: 480px) {
    margin-top: 1.25rem;
  }
`;

const AgreeButton = styled.button`
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  min-width: 200px;

  &:hover {
    background: linear-gradient(135deg, #218838 0%, #1aa179 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(40, 167, 69, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.75rem;
    font-size: 1rem;
    min-width: 180px;
  }

  @media (max-width: 480px) {
    padding: 0.7rem 1.5rem;
    font-size: 0.95rem;
    min-width: 160px;
    width: 100%;
    max-width: 280px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e0e0e0;
    transform: rotate(90deg);
  }

  @media (max-width: 480px) {
    top: 1rem;
    right: 1rem;
    width: 28px;
    height: 28px;
  }
`;

const CloseIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: #666;

  ${CloseButton}:hover & {
    fill: #333;
  }
`;

interface TermsAndConditionsModalProps {
  onAgree: () => void;
  onClose?: () => void;
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ 
  onAgree, 
  onClose 
}) => {
  // Prevent background scrolling when modal is open
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <TermsOverlay onClick={handleOverlayClick}>
      <TermsContent>
        {onClose && (
          <CloseButton onClick={onClose} aria-label="Close terms and conditions">
            <CloseIcon viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </CloseIcon>
          </CloseButton>
        )}
        
        <TermsTitle>Our Policy</TermsTitle>
        
        <TermsParagraph>
          Welcome to Grema. By registering and paying for a stall, you agree to the following terms and conditions:
        </TermsParagraph>

        <TermsList>
          <TermsListItem>
            <strong>1. Accuracy of Information:</strong> You are kindly requested to ensure that all information provided during registration, including business details, contact information, and media, is accurate and up to date.
          </TermsListItem>

          <TermsListItem>
            <strong>2. Content Standards:</strong> All content, including images, descriptions, and listings, must be appropriate for a general audience and not violate any local laws or regulations. Prohibited content includes, but is not limited to, fraudulent, illegal, offensive, or copyrighted material without proper authorization.
          </TermsListItem>

          <TermsListItem>
            <strong>3. Subscription and Payment:</strong> Your stall is activated upon successful payment of the subscription fee. The subscription is valid for the period paid for, i.e., 1 month, 6 months, and 12 months. Renewal is required to maintain your stall's active status.
          </TermsListItem>

          <TermsListItem>
            <strong>4. Standard Package:</strong> When you pay for advertising space, please note that you will be given a maximum of 10 images (item photos or general business photos) and a maximum of 2 videos (not exceeding 1 minute per video). You can change/update your adverts anytime you want if your subscription is still due.
          </TermsListItem>

          <TermsListItem>
            <strong>5. Premium Package:</strong> When you pay for Premium advertising space, please note that you will be given a maximum of 15 images (item photos or general business photos) and a maximum of 5 videos (not exceeding 1 minute per video). Premium adverts will also appear on the front page of the website and will keep reshuffling. You can change/update your adverts anytime you want if your subscription is still due.
          </TermsListItem>

          <TermsListItem>
            <strong>6. Termination:</strong> Grema reserves the right to suspend or terminate any stall registration without refund if the user violates these terms and conditions.
          </TermsListItem>

          <TermsListItem>
            <strong>7. Liability:</strong> Regarding the advertising arm, Grema is a platform for advertising and connecting businesses and customers. We are not responsible for any transactions, disputes, or liabilities arising between business owners and customers.
          </TermsListItem>
          
          <TermsListItem>
            <strong>8. Business Verification:</strong> Please note that GREMA will physically verify your business either remotely or physically through its field staff. This is to ensure that your business is genuine and also to help GREMA be able to direct customers to access your business and services easily.
          </TermsListItem>

          <TermsListItem>
            <strong>9. Exceptions on Specific Advertisements:</strong> To protect customers from fraudulent opportunists, GREMA requires advertisers of Cars, Land and all forms of buildings to provide Logbooks, land titles and photocopies of national IDs/Passports bearing respective actual names of advertisers. This information is for GREMA verification purposes only and not for public consumption. Also, for this category of property, business owners will only advertise one item per subscription. gremaglobal.com will also not advertise products that are illegal in respective governments. Please ensure that you advertise products and services within the legal frameworks of your respective governments.
          </TermsListItem>

          <TermsListItem>
            <strong>10. Consent:</strong> By clicking "I Agree," you acknowledge that you have read, understood, and accept these terms.
          </TermsListItem>
        </TermsList>

        <ButtonContainer>
          <AgreeButton onClick={onAgree}>I Agree and Continue</AgreeButton>
        </ButtonContainer>
      </TermsContent>
    </TermsOverlay>
  );
};

export default TermsAndConditionsModal;